import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'antd';

import FormulaInput from '../common/FormulaInput';
import EventFormElement from '../events/EventFormElement';
import { flattenTokenNodes } from '../common/tokenTemplate';
import { formatValue } from '../items/itemValueDisplay';
import { getDataTypeMeta } from '../../utils/dictionary';
import { isElementValueEmpty } from '../../utils/elements';
import { validateElementValue } from '../../utils/elementValidation';
import {
	composerTokens,
	pillType,
	pillWords,
	readToken,
	staticToken,
	takesParts,
} from './composerModel';
import TransformDialog from './TransformDialog';
import {
	CANCEL,
	FIXED_VALUE,
	FIXED_VALUE_APPLY,
	PLACEHOLDER_ONE,
	PLACEHOLDER_TEXT,
	PREVIEW_HEAD,
	PREVIEW_STALE,
	composerLabel,
	expectedLine,
	fixedTitle,
	previewRow,
} from './composerCopy';

const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
const labelOf = el => {
	const label = el?.label;
	const text = label && typeof label === 'object' ? label.singular : label;
	return typeof text === 'string' && text.trim() ? text.trim() : el?.name || '';
};
const typeWords = el => getDataTypeMeta(el?.dataType || 'text-small')?.label || 'Short text';
const inner = token => String(token).slice(2, -2).trim();

/** The preview's value as the element writes it, or null when the item has none. */
function shown(element, raw) {
	if (raw === null || raw === undefined || raw === '') return null;
	const text = formatValue(element, raw);
	if (text !== '' && text !== null && text !== undefined) return String(text);
	if (Array.isArray(raw)) return raw.join(', ');
	return typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
}

/**
 * A target element built from pills, words and transforms (KPE-028, KPR-708):
 * the formula editor's own pill editor in its template mode, with the card
 * change's vocabulary. A text takes any number of pills and words in any
 * order; any other type takes one pill, one transform or one fixed value. The
 * words become a fill in `composerModel.js`; the server's sentence
 * for a refusal is the row's (it comes with the dry run), the local one is
 * `problem`.
 *
 *   target, sources, sourcesByName, getElementsForCard   the cards
 *   text, onChange(text)      the composer's text
 *   problem                   the one sentence the composer itself refuses with
 *   preview                   the dry run's real items, `[{ id, title, before, after }]`
 *   stale                     the preview belongs to an older mapping
 */
export default function Composer({
	target,
	sources,
	sourcesByName,
	getElementsForCard,
	text,
	onChange,
	problem,
	preview = [],
	stale = false,
}) {
	const editorRef = useRef(null);
	const [transform, setTransform] = useState(null); // { token, index }
	const [fixedOpen, setFixedOpen] = useState(false);
	const [fixedValue, setFixedValue] = useState(undefined);

	const label = labelOf(target);
	const textual = takesParts(target);

	const tokens = useMemo(
		() => composerTokens(sources, { getElementsForCard, target }),
		[sources, getElementsForCard, target]
	);
	const baseLabels = useMemo(() => new Map(flattenTokenNodes(tokens).map(node => [inner(node.token), node.label])), [tokens]);
	// A pill's words: its element's label, plus its transform; a fixed value shows the value.
	const tokenLabel = useCallback(token => pillWords(
		token,
		base => baseLabels.get(base) || base,
		value => `Fixed: ${shown(target, value) ?? '—'}`
	), [baseLabels, target]);

	const onPillEdit = useCallback((token, index) => {
		const pill = readToken(token);
		if (pill.fixed || pill.broken) {
			setFixedValue(pill.fixed ? pill.value : undefined);
			setFixedOpen(true);
			return;
		}
		setTransform({ token, index });
	}, []);

	const transformInfo = transform ? pillType(readToken(transform.token).base, { sourcesByName, getElementsForCard }) : null;
	const previewItems = useMemo(
		() => (preview || []).filter(item => item && item.before).map(item => ({ title: item.title, before: item.before })),
		[preview]
	);

	const applyTransform = token => {
		const at = transform ? transform.index : -1;
		setTransform(null);
		if (at >= 0 && editorRef.current) editorRef.current.replaceToken(at, token);
	};

	const fixedProblem = fixedValue !== undefined && !isElementValueEmpty(target, fixedValue) ? validateElementValue(target, fixedValue) : null;
	const applyFixed = () => {
		if (isElementValueEmpty(target, fixedValue) || fixedProblem) return;
		setFixedOpen(false);
		onChange(staticToken(fixedValue));
	};

	// What the row would hold for a few real items, from the dry run.
	const rows = useMemo(() => (preview || [])
		.map(item => ({ id: item.id, title: item.title, text: hasOwn(item.after, target.name) ? shown(target, item.after[target.name]) : null }))
		.filter(row => row.text !== null)
		.slice(0, 3), [preview, target]);

	return (
		<div className="change-card__composer">
			<FormulaInput
				ref={editorRef}
				template
				suggest={false}
				multiline={target?.dataType === 'text-large' || target?.dataType === 'rich-text'}
				value={text}
				onChange={onChange}
				tokens={tokens}
				tokenLabel={tokenLabel}
				onPillEdit={onPillEdit}
				ariaLabel={composerLabel(label)}
				placeholder={textual ? PLACEHOLDER_TEXT : PLACEHOLDER_ONE}
				invalid={!!problem}
			/>
			<p className="change-card__note change-card__expects">{expectedLine(typeWords(target), textual)}</p>
			{!textual ? (
				<Button size="small" type="link" className="change-card__fixed" onClick={() => { setFixedValue(undefined); setFixedOpen(true); }}>
					{FIXED_VALUE}
				</Button>
			) : null}
			{problem ? <div className="change-card__reason" role="alert">{problem}</div> : null}
			{rows.length ? (
				<div className={`change-card__live-preview${stale ? ' is-stale' : ''}`} aria-label={PREVIEW_HEAD}>
					<p className="change-card__preview-side">{PREVIEW_HEAD}{stale ? ` · ${PREVIEW_STALE}` : ''}</p>
					<ul>
						{rows.map(row => <li key={row.id}>{previewRow(row.title, row.text)}</li>)}
					</ul>
				</div>
			) : null}

			<TransformDialog
				open={!!transform}
				token={transform ? transform.token : null}
				info={transformInfo}
				words={transform ? tokenLabel(`{{${readToken(transform.token).base}}}`) : ''}
				sources={sources}
				preview={previewItems}
				onApply={applyTransform}
				onCancel={() => setTransform(null)}
			/>

			<Modal
				open={fixedOpen}
				title={fixedTitle(label)}
				onCancel={() => setFixedOpen(false)}
				onOk={applyFixed}
				okText={FIXED_VALUE_APPLY}
				cancelText={CANCEL}
				okButtonProps={{ disabled: isElementValueEmpty(target, fixedValue) || !!fixedProblem }}
				destroyOnClose
				width={480}
				style={{ maxWidth: 'calc(100vw - 32px)' }}
				className="change-card__dialog"
			>
				<EventFormElement
					event={{ _id: null, elements: { [target.name]: fixedValue } }}
					element={target}
					value={fixedValue}
					onChange={(name, value) => setFixedValue(value)}
					showIconBadge={false}
					errorMessage={fixedProblem || null}
				/>
			</Modal>
		</div>
	);
}
